import { useSyncExternalStore } from 'react';

/**
 * The order this browser placed and has not paid yet. The payment step lives on the checkout page,
 * so without this a refresh or a closed tab lost the way back to paying it. Kept until the order
 * is paid, canceled or otherwise done (checked against the tracking page's answer).
 */
export interface UnpaidOrder {
  orderId: string;
  token: string;
  orderNumber: string;
  placedAt: number;
}

const KEY = 'crave-unpaid-order';
/** Holds last minutes, not days: anything older has certainly been released. */
const MAX_AGE_MS = 2 * 60 * 60_000;

function read(): UnpaidOrder | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<UnpaidOrder>;
    if (
      typeof v.orderId !== 'string' ||
      typeof v.token !== 'string' ||
      typeof v.placedAt !== 'number'
    )
      return null;
    if (Date.now() - v.placedAt > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return {
      orderId: v.orderId,
      token: v.token,
      orderNumber: v.orderNumber ?? '',
      placedAt: v.placedAt,
    };
  } catch {
    return null;
  }
}

let current: UnpaidOrder | null = read();
const listeners = new Set<() => void>();

function set(next: UnpaidOrder | null) {
  current = next;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
  for (const l of listeners) l();
}

export const unpaidOrder = {
  get: () => current,
  remember: (o: Omit<UnpaidOrder, 'placedAt'>) => set({ ...o, placedAt: Date.now() }),
  /** Forget it, but only if it is still this order. */
  forget: (orderId: string) => {
    if (current?.orderId === orderId) set(null);
  },
};

export function useUnpaidOrder(): UnpaidOrder | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => current,
  );
}

export const trackPathFor = (token: string) => `/order/${token}`;
